import { FieldViewRouteData, KpFeatureMeta } from '@app/core/shared/components/field-view/models/field-view';
import { LayersTypes } from '@app/core/shared/enums/field-view-layer.types';
import { Feature, Map } from 'ol';
import { click, pointerMove } from 'ol/events/condition';
import { LineString } from 'ol/geom';
import { Select } from 'ol/interaction';
import VectorLayer from 'ol/layer/Vector';
import { transform } from 'ol/proj';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Text } from 'ol/style';
import MapViewUtility from '../utilities/map-view.utility';
import { FieldViewControlService } from '../services/field-view-control.service';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';

const kpLineLength = 1000;
const lineThickness = 2;
const hitTolerance = 30;

const createTextStyle = (text: string, color = '#2E273375', fontSize = '14px'): Text =>
  new Text({
    text,
    font: `${fontSize} Calibri,sans-serif`,
    fill: new Fill({ color }),
    padding: [2, 2, 2, 2],
    placement: 'line',
    textAlign: 'end',
    offsetY: -12,
    offsetX: -12
  });

export const styleFactory = (meta: KpFeatureMeta) =>
  new Style({
    stroke: new Stroke({ color: '#2E273375', width: lineThickness }),
    text: createTextStyle(meta.label)
  });

const createKPFeature = (
  start: number[],
  end: number[],
  kpFrom: number,
  kpTo: number,
  label: string
): Feature<LineString> => {
  const lineString = new LineString([start, end]);
  const style = styleFactory({ start, end, kpFrom, kpTo, label });
  const feature = new Feature(lineString);
  feature.set('start', start);
  feature.set('end', end);
  feature.set('kpFrom', kpFrom);
  feature.set('kpTo', kpTo);
  feature.set('label', label);
  feature.setStyle(style);
  return feature;
};

export function buildKpLayerFeatures(
  data: FieldViewRouteData[],
  projectionZone: string,
  kpSpacing: number,
  controlService: FieldViewControlService
) {
  const features: Feature<LineString>[] = [];

  if (kpSpacing === 0) {
    controlService.setKpFeatures([]);
    return;
  }

  if (data.length > 0) {
    const initialPoint = data[0];
    const transformedInitialPoint = transform(
      [initialPoint.easting, initialPoint.northing],
      projectionZone,
      MapViewUtility.getDefaultMapProjection()
    );
    const initialkpTo = initialPoint.kp + kpSpacing;

    const unitX = kpLineLength;
    const unitY = kpLineLength;

    const start = [transformedInitialPoint[0] - unitX, transformedInitialPoint[1] - unitY];
    const end = [transformedInitialPoint[0] + unitX, transformedInitialPoint[1] + unitY];

    const initialKpFeature = createKPFeature(
      start,
      end,
      initialPoint.kp,
      initialkpTo,
      `KP ${initialPoint.kp.toFixed(2)}`
    );

    features.push(initialKpFeature);

    const endKP = data[data.length - 1].kp;

    for (let currentKP = initialkpTo; currentKP < endKP; currentKP += kpSpacing) {
      const currentPointIndex = data.findIndex(point => point.kp >= currentKP);
      if (currentPointIndex > 0) {
        const transformedPoint = transform(
          [data[currentPointIndex].easting, data[currentPointIndex].northing],
          projectionZone,
          MapViewUtility.getDefaultMapProjection()
        );

        const lastTransformedPoint = transform(
          [data[currentPointIndex - 1].easting, data[currentPointIndex - 1].northing],
          projectionZone,
          MapViewUtility.getDefaultMapProjection()
        );

        const dx = transformedPoint[0] - lastTransformedPoint[0];
        const dy = transformedPoint[1] - lastTransformedPoint[1];

        const length = Math.sqrt(dx * dx + dy * dy);
        const unitX = (-dy / length) * kpLineLength;
        const unitY = (dx / length) * kpLineLength;

        const midpoint = [
          (transformedPoint[0] + lastTransformedPoint[0]) / 2,
          (transformedPoint[1] + lastTransformedPoint[1]) / 2
        ];

        const start = [midpoint[0] - unitX, midpoint[1] - unitY];
        const end = [midpoint[0] + unitX, midpoint[1] + unitY];

        const kpFeature = createKPFeature(
          start,
          end,
          currentKP,
          currentKP + kpSpacing,
          `KP ${currentKP.toFixed(2)}`
        );

        features.push(kpFeature);
      }
    }
  }

  controlService.setKpFeatures(features);
}
function setupKpLayerCursorPointer(map: Map, vectorLayer: VectorLayer): void {
  const mapTarget = map.getTargetElement
    ? map.getTargetElement()
    : typeof map.getTarget === 'function'
      ? document.getElementById(map.getTarget() as string)
      : null;

  if (!mapTarget) {
    return;
  }

  const pointerMoveHandler = (evt: any) => {
    let isOverKpFeature = false;
    map.forEachFeatureAtPixel(
      evt.pixel,
      (feature, layer) => {
        if (layer === vectorLayer) {
          isOverKpFeature = true;
          return true;
        }
        return false;
      },
      {
        hitTolerance
      }
    );

    mapTarget.style.cursor = isOverKpFeature ? 'pointer' : '';
  };

  map.on('pointermove', pointerMoveHandler);
}

export const drawInteractiveKPLayer = (map: Map, controlService: FieldViewControlService): VectorLayer => {
  const vectorSource = new VectorSource({
    features: controlService.kpFeatures().slice(),
    strategy: bboxStrategy
  });

  const vectorLayer = new VectorLayer({
    properties: { layerType: LayersTypes.INTERACTIVE_KP_LAYER },
    source: vectorSource
  });

  const hoverInteraction = new Select({
    condition: pointerMove,
    layers: [vectorLayer],
    hitTolerance,
    style: feature => {
      const textStyle = createTextStyle(
        `KP ${feature.get('kpFrom').toFixed(2)} - KP ${feature.get('kpTo').toFixed(2)}`,
        '#DCB9F8'
      );
      return new Style({
        stroke: new Stroke({ color: '#DCB9F8', width: lineThickness }),
        text: textStyle
      });
    }
  });
  map.addInteraction(hoverInteraction);

  const clickInteraction = new Select({
    condition: click,
    layers: [vectorLayer],
    hitTolerance
  });

  clickInteraction.on('select', event => {
    const feature = event.selected[0];
    if (feature) {
      const kpFrom = feature.get('kpFrom');
      const kpTo = feature.get('kpTo');
      controlService.setKpRangeClicked({ kpFrom: Number(kpFrom), kpTo: Number(kpTo) });
    }
  });
  map.addInteraction(clickInteraction);
  setupKpLayerCursorPointer(map, vectorLayer);

  return vectorLayer;
};
